import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFocusEffect, useNavigation } from '@react-navigation/core'
import { Image, ImageStyle } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Pill } from '@components/base'
import { Feature, TouchableSection, useFeatureFlag } from '@components'
import { IconSource, IconSources } from '@src/assets/icons'
import { ketoMojoSyncStatusSelector, terraProvidersSelector } from '@src/selectors/integrations'
import { Device } from '@src/config'
import { TerraProviderKind, UiPillSize } from '@src/types'
import { TerraProvider } from '../../models/settings.types'
import { IntegrationTouchableSection } from './IntegrationTouchableSection'
import { providerBrandIcon, providerBrandName } from './providerBranding'
import { getProviderPillProps } from './terraProviderStatus'

export const PROVIDER_ICONS: Partial<Record<TerraProviderKind, IconSource>> = {
  [TerraProviderKind.Garmin]: IconSources.garminIcon,
  [TerraProviderKind.Oura]: IconSources.ouraIcon,
  [TerraProviderKind.FreeStyleLibre]: IconSources.freeStyleLibreIcon,
  [TerraProviderKind.MyFitnessPal]: IconSources.myFitnessPal,
  [TerraProviderKind.KetoMojoUs]: IconSources.ketomojo,
  [TerraProviderKind.HealthConnect]: IconSources.healthConnect,
  [TerraProviderKind.GoogleHealth]: IconSources.googleHealthIcon,
}

export const TerraProviders = () => {
  const styles = useStyleSheet(themedStyle)
  const dispatch = useDispatch()
  const navigation = useNavigation()
  const providers = useSelector(terraProvidersSelector)
  const ketoMojoSync = useSelector(ketoMojoSyncStatusSelector)
  const ketoMojoTerraFlag = useFeatureFlag(Feature.TerraKetoMojo)

  const healthConnectProvider = providers.find(
    (provider) => provider.providerKind === TerraProviderKind.HealthConnect,
  )

  const hideTerraKetoMojo = ketoMojoSync || !ketoMojoTerraFlag
  const regularProviders = providers.filter(
    (provider) =>
      provider.providerKind !== TerraProviderKind.AppleHealth &&
      provider.providerKind !== TerraProviderKind.HealthConnect &&
      (hideTerraKetoMojo ? provider.providerKind !== TerraProviderKind.KetoMojoUs : true),
  )

  useFocusEffect(
    useCallback(() => {
      dispatch({
        type: 'settings/fetchTerraProviders',
      })
    }, [dispatch]),
  )

  const renderProviderStatusPill = (provider: TerraProvider) => {
    const pillProps = getProviderPillProps(provider)
    if (!pillProps) {
      return
    }

    return (
      <Pill
        text={pillProps.text}
        status={pillProps.status}
        size={UiPillSize.S}
        style={styles.tag}
      />
    )
  }

  const onProviderPress = (item: TerraProvider) => {
    navigation.navigate('TerraProvider', { provider: item.provider })
  }

  return (
    <>
      {Device.hasHealthKit && (
        <IntegrationTouchableSection
          sectionTitle="Apple Health"
          imageSource={IconSources.appleHealth}
          onPress={() => navigation.navigate('HealthKitIntegration')}
        />
      )}
      {Device.android && healthConnectProvider && (
        <TouchableSection
          key={healthConnectProvider.provider}
          accessoryLeft={
            <Image
              source={PROVIDER_ICONS[TerraProviderKind.HealthConnect] as IconSource}
              style={styles.providerIcon as ImageStyle}
            />
          }
          title={healthConnectProvider.name}
          showRightArrow
          style={styles.providersSection}
          tagComponent={renderProviderStatusPill(healthConnectProvider)}
          onPress={() => onProviderPress(healthConnectProvider)}
        />
      )}
      {regularProviders.map((provider) => (
        <TouchableSection
          key={provider.provider}
          accessoryLeft={
            <Image
              source={providerBrandIcon(
                provider.providerKind,
                PROVIDER_ICONS[provider.providerKind] as IconSource,
              )}
              style={styles.providerIcon as ImageStyle}
            />
          }
          title={providerBrandName(provider.providerKind, provider.name)}
          showRightArrow
          style={styles.providersSection}
          tagComponent={renderProviderStatusPill(provider)}
          onPress={() => onProviderPress(provider)}
        />
      ))}
    </>
  )
}

const themedStyle = StyleService.create({
  providersSection: {
    borderBottomWidth: 1,
    borderBottomColor: 'theme.surface.base',
  },
  providerIcon: {
    width: 24,
    height: 24,
    marginRight: 12,
  },
  tag: {
    marginRight: 16,
  },
})
